import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './RoutePopup.module.css';

const ACTIVITY_TAGS = [
  'Hiking',
  'Scrambling',
  'Glacier travel',
  'Ice climbing',
  'Via ferrata'
];

const DIFFICULTIES = [
  'F', 'PD-', 'PD', 'PD+', 'AD-', 'AD', 'AD+',
  'D-', 'D', 'D+', 'TD-', 'TD', 'TD+',
  'ED-', 'ED', 'ED+', 'ABO'
];

export default function EditRoutePopup({ route, mountains, onClose, onSave }) {
  const { t } = useTranslation();
  const [name, setName] = useState(route?.name || '');
  const [type, setType] = useState(route?.type || 'return'); 
  const [startPoint, setStartPoint] = useState(route?.startPoint || '');
  const [startColor, setStartColor] = useState(route?.startColor || '#000000');
  const [hasStartColor, setHasStartColor] = useState(!!route?.startColor);
  const [endPoint, setEndPoint] = useState(route?.endPoint || '');
  const [waypoints, setWaypoints] = useState([]);
  const [description, setDescription] = useState(route?.description || '');
  const [kms, setKms] = useState(route?.kms || '');
  const [elevationGain, setElevationGain] = useState(route?.elevationGain || '');
  const [tags, setTags] = useState(route?.tags || []);
  const [difficulty, setDifficulty] = useState(route?.difficulty || '');
  const [routeType, setRouteType] = useState(route?.routeType || 'one-day');
  const [submitting, setSubmitting] = useState(false);

  // Mountain Tagging
  const [selectedMountainTags, setSelectedMountainTags] = useState([]);
  const [mountainInputText, setMountainInputText] = useState('');
  const [mentionQuery, setMentionQuery] = useState(null);
  const [activeSuggestion, setActiveSuggestion] = useState(0);
  const [suggestions, setSuggestions] = useState([]);

  useEffect(() => {
    if (route) {
      setName(route.name || '');
      setType(route.type || 'return');
      setStartPoint(route.startPoint || '');
      setStartColor(route.startColor || '#000000');
      setHasStartColor(!!route.startColor);
      setEndPoint(route.endPoint || '');
      setDescription(route.description || '');
      setKms(route.kms || '');
      setElevationGain(route.elevationGain || '');
      setDifficulty(route.difficulty || '');
      setRouteType(route.routeType || 'one-day');
      setTags(route.tags || []);
      
      if (Array.isArray(route.waypoints)) {
        setWaypoints(route.waypoints.map(wp => {
          if (typeof wp === 'object' && wp !== null) {
            return { name: wp.name || '', color: wp.color || '#000000', hasColor: !!wp.color };
          }
          return { name: String(wp), color: '#000000', hasColor: false };
        }));
      } else {
        setWaypoints([]);
      }
    }
  }, [route]); // Only run when route object reference changes (e.g. opening popup)

  useEffect(() => {
     // Separate effect for initializing mountain tags to avoid resetting form when mountains prop loads
     if (route) {
        if (Array.isArray(route.mountains)) {
            setSelectedMountainTags(route.mountains);
        } else if (route.mountainIds && mountains) {
            // If detailed objects not present, fallback
            const matched = mountains.filter(m => route.mountainIds.includes(m.id) || route.mountainIds.includes(String(m.id)));
            setSelectedMountainTags(matched);
        } else {
            // Only reset if we have no info
            if (!selectedMountainTags.length) setSelectedMountainTags([]);
        }
     }
  }, [route, mountains]);

  const handleWaypointChange = (index, field, value) => {
    const newWaypoints = [...waypoints];
    newWaypoints[index] = { ...newWaypoints[index], [field]: value };
    setWaypoints(newWaypoints);
  };

  const addWaypoint = () => {
    setWaypoints([...waypoints, { name: '', color: '#000000', hasColor: false }]);
  };

  const removeWaypoint = (index) => {
    setWaypoints(waypoints.filter((_, i) => i !== index));
  };

  const handleTagToggle = (tag) => {
    setTags(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  // Mountain Tag Logic
  const handleMountainInput = (e) => {
    const val = e.target.value;
    setMountainInputText(val);

    const selectionEnd = e.target.selectionEnd;
    let start = selectionEnd - 1;
    while (start >= 0 && !/\s/.test(val[start])) {
      start--;
    }
    start++; 

    const word = val.slice(start, selectionEnd);
    if (word.startsWith('#')) {
      const query = word.slice(1).toLowerCase();
      setMentionQuery(query);
      setActiveSuggestion(0);

      const mMatches = mountains
        .filter(m => m.name.toLowerCase().includes(query))
        .filter(m => !selectedMountainTags.some(sel => sel.id === m.id))
        .slice(0, 5);
      
      setSuggestions(mMatches);
    } else {
      setMentionQuery(null);
      setSuggestions([]);
    }
  };

  const selectMountainTag = (m) => {
    setSelectedMountainTags(prev => [...prev, m]);
    setMountainInputText('');
    setMentionQuery(null);
    setSuggestions([]);
  };

  const handleKeyDown = (e) => {
    if (mentionQuery !== null && suggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveSuggestion(prev => (prev + 1) % suggestions.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveSuggestion(prev => (prev - 1 + suggestions.length) % suggestions.length);
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        selectMountainTag(suggestions[activeSuggestion]);
      } else if (e.key === 'Escape') {
        setMentionQuery(null);
      }
    } else if (e.key === 'Enter') {
        e.preventDefault();
    }
  };

  const removeMountainTag = (id) => {
      setSelectedMountainTags(selectedMountainTags.filter(m => m.id !== id));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !startPoint.trim()) {
      alert(t('mountainPage.routes.errors.fillRequired'));
      return;
    }
    
    if (selectedMountainTags.length === 0) {
       alert('Please tag at least one mountain.');
       return;
    }

    setSubmitting(true);
    try {
      const finalEndPoint = (endPoint && endPoint.trim()) ? endPoint.trim() : startPoint;
      await onSave({
        name,
        type: (finalEndPoint !== startPoint) ? 'oneway' : 'return',
        startPoint,
        startColor: hasStartColor ? startColor : null,
        endPoint: finalEndPoint,
        difficulty,
        routeType,
        waypoints: waypoints
          .filter(w => (typeof w.name === 'string' && w.name.trim()) || typeof w === 'string')
          .map(w => ({
            name: String(w.name || w).trim(),
            color: w.hasColor && w.color ? String(w.color).trim() : null
          })),
        description: description ? String(description).trim() : '',
        tags: Array.isArray(tags) ? tags : [],
        kms: kms ? parseFloat(kms) : null,
        elevationGain: elevationGain ? parseInt(elevationGain, 10) : null,
        mountainIds: selectedMountainTags.map(m => m.id)
      });
      onClose();
    } catch (err) {
      console.error(err);
      alert(t('mountainPage.routes.errors.update'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.popupOverlay}>
      <div className={styles.popup}>
        <button className={styles.closeBtn} onClick={onClose}>&times;</button>
        <h2>{t('mountainPage.routes.edit')}</h2>
        <form onSubmit={handleSubmit} className={styles.form}>
          
          <div className={styles.formGroup}>
            <label>{t('mountainPage.routes.name')}</label>
            <input 
              className={styles.input}
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Standard Route via South Ridge"
              required
              maxLength={50}
            />
          </div>

          <div className={styles.formGroup}>
            <label>Tagged Mountains (Required)</label>
            <div className={styles.tagInputWrapper}>
                <div className={styles.selectedTags}>
                    {selectedMountainTags.map(m => (
                        <span key={m.id} className={styles.mountainTag}>
                            {m.name}
                            <button type="button" onClick={() => removeMountainTag(m.id)}>&times;</button>
                        </span>
                    ))}
                </div>
                <div style={{ position: 'relative' }}>
                    <input 
                        className={styles.input}
                        value={mountainInputText}
                        onChange={handleMountainInput}
                        onKeyDown={handleKeyDown}
                        placeholder="Type #Name to add..."
                    />
                     {mentionQuery !== null && suggestions.length > 0 && (
                        <div className={styles.suggestionsList}>
                            <div className={styles.suggestionHeader}>Suggestions</div>
                            {suggestions.map((m, idx) => (
                                <div 
                                    key={m.id} 
                                    className={`${styles.suggestionItem} ${idx === activeSuggestion ? styles.suggestionItemActive : ''}`}
                                    onClick={() => selectMountainTag(m)}
                                >
                                    {m.name}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
          </div>

          <div className={styles.formGroup}>
            <label>{t('mountainPage.routes.startPoint')}</label>
            <div className={styles.pointRow}>
                <input 
                  className={styles.input}
                  value={startPoint}
                  onChange={e => setStartPoint(e.target.value)}
                  placeholder={t('mountainPage.routes.startPointPlaceholder', { defaultValue: 'e.g. Parking lot at base' })}
                  required
                  maxLength={50}
                />
                <div className={styles.colorWrapper}>
                    <input 
                        type="checkbox"
                        checked={hasStartColor}
                        onChange={e => setHasStartColor(e.target.checked)}
                        title={t('mountainPage.routes.addMarkingColor')}
                    />
                    {hasStartColor && (
                        <input
                            type="color"
                            value={startColor}
                            onChange={e => setStartColor(e.target.value)}
                            className={styles.colorInput}
                        />
                    )}
                </div>
            </div>
          </div>

          <div className={styles.formGroup}>
            <label>{t('mountainPage.routes.waypointsLabel')}</label>
            <div className={styles.waypointsList}>
              {waypoints.map((wp, idx) => (
                <div key={idx} className={styles.waypointRow}>
                  <input 
                    className={styles.input}
                    value={wp.name}
                    onChange={e => handleWaypointChange(idx, 'name', e.target.value)}
                    placeholder={t('mountainPage.routes.waypointPlaceholder')}
                    maxLength={50}
                  />
                  <div className={styles.colorWrapper}>
                    <input 
                        type="checkbox"
                        checked={wp.hasColor}
                        onChange={e => handleWaypointChange(idx, 'hasColor', e.target.checked)}
                        title={t('mountainPage.routes.addMarkingColor')}
                    />
                    {wp.hasColor && (
                        <input
                            type="color"
                            value={wp.color}
                            onChange={e => handleWaypointChange(idx, 'color', e.target.value)}
                            className={styles.colorInput}
                        />
                    )}
                  </div>
                  {waypoints.length > 0 && (
                    <button 
                      type="button" 
                      className={styles.removeBtn}
                      onClick={() => removeWaypoint(idx)}
                      title={t('common.remove', { defaultValue: 'Remove' })}
                    >
                      &times;
                    </button>
                  )}
                </div>
              ))}
              <button type="button" className={styles.addBtn} onClick={addWaypoint}>
                + {t('mountainPage.routes.addWaypoint')}
              </button>
            </div>
          </div>

            <div className={styles.formGroup}>
              <label>{t('mountainPage.routes.endPoint')}</label>
              <input 
                className={styles.input}
                value={endPoint}
                onChange={e => setEndPoint(e.target.value)}
                placeholder={t('mountainPage.routes.endPointPlaceholder', { defaultValue: 'e.g. Summit or other side' })}
                maxLength={50}
              />
            </div>

          <div className={styles.formGroup} style={{ display: 'flex', gap: '1rem' }}>
            <div style={{ flex: 1 }}>
              <label>{t('mountainPage.routes.kms')}</label>
              <input
                className={styles.input}
                type="number"
                step="0.1"
                min="0"
                value={kms}
                onChange={e => setKms(e.target.value)}
                placeholder={t('mountainPage.routes.kmsPlaceholder', { defaultValue: 'e.g. 5.2' })}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label>{t('mountainPage.routes.elevationGain')}</label>
              <input
                className={styles.input}
                type="number"
                min="0"
                value={elevationGain}
                onChange={e => setElevationGain(e.target.value)}
                placeholder={t('mountainPage.routes.elevationGainPlaceholder', { defaultValue: 'e.g. 1200' })}
              />
            </div>
          </div>



          <div className={styles.formGroup} style={{ display: 'flex', gap: '1rem' }}>
             <div style={{flex: 1}}>
                <label>
                    {t('mountainPage.routes.difficulty')}
                    <span style={{ marginLeft: '0.5rem', fontWeight: 'normal', fontSize: '0.9rem', opacity: 0.8 }}>
                        ({t('mountainPage.routes.check')}
                        <a
                        href="https://www.bergfreunde.eu/alpine-grades-calculator/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.difficultyInfoLink}
                        title={t('mountainPage.details.difficultyInfo')}
                        style={{ marginLeft: '4px', color: 'inherit', textDecoration: 'none' }}
                        >
                        ?
                        </a>
                        )
                    </span>
                </label>
                <select 
                   className={styles.input}
                   value={difficulty}
                   onChange={e => setDifficulty(e.target.value)}
                >
                    <option value="">{t('mountainPage.routes.select')}</option>
                    {DIFFICULTIES.map(diff => (
                      <option key={diff} value={diff}>{diff}</option>
                    ))}
                </select>
             </div>
             <div style={{flex: 1}}>
                <label>{t('mountainPage.routes.type')}</label>
                <select 
                   className={styles.input}
                   value={routeType}
                   onChange={e => setRouteType(e.target.value)}
                >
                    <option value="one-day">{t('mountainPage.routes.types.oneDay')}</option>
                    <option value="multi-day-huts">{t('mountainPage.routes.types.multiDayHuts')}</option>
                </select>
             </div>
          </div>

          <div className={styles.formGroup}>
            <label>{t('mountainPage.routes.description')}</label>
            <textarea 
              className={styles.textarea}
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder={t('mountainPage.routes.descriptionPlaceholder')}
              maxLength={1000}
            />
          </div>

          <div className={styles.formGroup}>
            <label>{t('mountainPage.routes.activityTags')}</label>
            <div className={styles.checkboxGroup}>
              {ACTIVITY_TAGS.map(tag => (
                <label key={tag} className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={tags.includes(tag)}
                    onChange={() => handleTagToggle(tag)}
                  />
                  {t(`mountainPage.routes.tags.${tag}`, { defaultValue: tag })}
                </label>
              ))}
            </div>
          </div>

          <div className={styles.actions}>
            <button type="button" className={styles.cancelBtn} onClick={onClose}>
              {t('action.cancel')}
            </button>
            <button type="submit" disabled={submitting} className={styles.submitBtn}>
              {submitting ? t('mountainPage.routes.submittingSave') : t('mountainPage.routes.submitEdit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
